import styles from './Roles.module.css';

import * as api from '../../api.js';

import { useAuth } from '../../AuthContext';
import { RolesHeader } from './RolesHeader';
import { RolesList } from './RolesList';

import { useState, useEffect } from 'react';

export const Roles = ({ type, readOnly, onRolesLoaded }) => {
    const [ roles, setRoles ] = useState([]);
    const [ certifications, setCertifications ] = useState([]);
    const [ loading, setLoading ] = useState(true);
    const [ error, setError ] = useState(null);

    const { token } = useAuth();

    const handleNewRole = () => {
        setRoles([ ...roles, { id: 'new', type: '', name: '', certification: '', required: '', lead: false } ]);
    };

    const handleAddRole = async (role) => {
        try {
            setLoading(true);

            const newRoleId = await api.createRole({ ...role, type: type }, token);
            const updatedRoles = [ ...roles.filter(r => r.id !== 'new'), { ...role, id: newRoleId, type: type } ];

            if (newRoleId) {
                setRoles(updatedRoles);
            }

            onRolesLoaded(updatedRoles.filter(r => r.type === type));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateRole = async (role) => {
        try {
            setLoading(true);
            
            const updatedRole = { ...role, type: type };
            
            if (await api.updateRole(updatedRole, token)) {
                const updatedRoles = [ ...roles.map(r => r.id === role.id ? updatedRole : r) ];

                if (updatedRole.error) {
                    throw new Error(updatedRole.error);
                }

                setRoles(updatedRoles);

                onRolesLoaded(updatedRoles.filter(r => r.type === type));
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveRole = async (role) => {
        try {
            setLoading(true);

            const response = await api.removeRole(role.id, token);

            if (response.error) {
                throw new Error(response.error);
            }

            const updated = roles.filter(s => s.id !== role.id);
                
            setRoles(updated);

            onRolesLoaded(updated.filter(r => r.type === type));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const certsData = await api.getCertifications(token);
                const rolesData = await api.getAllRoles(token);

                if (certsData.error) {
                    setCertifications([{ id: 'none', name: 'Could not load certifications' }]);
                } else {
                    setCertifications(certsData);
                }
                
                if (rolesData.error) {
                    throw new Error(rolesData.error);
                }

                setRoles(rolesData.filter(role => role.type === type));

                onRolesLoaded(rolesData.filter(role => role.type === type));
            } catch (err) {
                console.error(err);

                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [ ]);
    
    if (loading) {
        return <p>Loading roles...</p>;
    }

    if (error) {
        return <p>Error loading roles: { error }</p>;
    }

    return (
        <>
            <RolesHeader count={ roles.length } readOnly={ readOnly } onNewRole={ handleNewRole } />

            { roles.length > 0 || readOnly ?
                < RolesList
                    readOnly={ readOnly }
                    roles={ roles }
                    certifications={ certifications }
                    onAddRole={ handleAddRole }
                    onUpdateRole={ handleUpdateRole}
                    onRemoveRole={ handleRemoveRole }
                    onCancel={ () => setRoles(prev => prev.filter(role => role.id !== 'new'))
                } />
                : <div className={ styles.roles }><span className={ styles.warn } onClick={ handleNewRole }>add roles to create shifts</span></div>
            }
        </>
    );
};